/**
 * "Điều chỉnh số dư" (owner decision 2026-10-02): what the dialog sends to
 * `POST /customers/{id}/wallet/adjustments`, and who sees the action. Plain functions so the
 * tests can check the request shape without rendering (tests/wallet-adjustment.test.mjs).
 */

export type AdjustDirection = 'CREDIT' | 'DEBIT';

export interface AdjustDraft {
  direction: AdjustDirection;
  /** As typed: "1.000.000", "1 000 000" and "1000000" are the same VND amount. */
  amount: string;
  currency: string;
  reason: string;
  reference: string;
}

/** The request body: `amount` signed (negative = "Trừ"), whole minor units. */
export interface WalletAdjustmentRequest {
  amount: string;
  currency: string;
  reason: string;
  /** The transaction code (required, owner decision 2026-10-05 §3). */
  reference: string;
  /** Staff checked that the code recorded before is not the same money. */
  acknowledge_duplicate?: boolean;
}

export type AdjustProblem = 'amount' | 'amountTooLarge' | 'reason' | 'reference';

/** Above this many đồng (either way), or in another currency, a second person approves first (owner decision 2026-10-05 §4). */
export const LARGE_ADJUSTMENT_VND = 50_000_000;

/** Whether the server will ask for a second person's approval of this request. */
export function needsSecondApproval(r: WalletAdjustmentRequest): boolean {
  return r.currency !== 'VND' || Math.abs(Number(r.amount)) > LARGE_ADJUSTMENT_VND;
}

/** The ledger stores NUMERIC(20,6): at most 14 integer digits (the server refuses more, `TOO_LARGE`). */
const MAX_INTEGER_DIGITS = 14;

/** Minor-unit digits, as the server rounds them (`Currency::minor_units`, crates/common/src/money.rs). */
const MINOR_UNITS: Record<string, number> = { VND: 0, JPY: 0, KRW: 0, BHD: 3, KWD: 3, OMR: 3, TND: 3 };

export const minorUnits = (currency: string): number => MINOR_UNITS[currency] ?? 2;

/**
 * The amount field's hint and error for the billing currency: whole units ("1.000.000") or its
 * minor units ("1,250.50"), with the values they name.
 */
export function amountText(currency: string) {
  const digits = minorUnits(currency);
  return digits === 0
    ? ({ hint: 'walletAdjust.amountHint', invalid: 'walletAdjust.amountInvalid', values: { currency, digits, example: '1.000.000' } } as const)
    : ({ hint: 'walletAdjust.amountHintMinor', invalid: 'walletAdjust.amountInvalidMinor', values: { currency, digits, example: `1,250.${'5'.padEnd(digits, '0')}` } } as const);
}

/**
 * The positive amount as a plain decimal string, or null. Thousands may be grouped by threes:
 * whole-unit currencies (VND) with `.`, `,` or a space ("1.000.000"), others with `,` or a space
 * and `.` before at most their minor-unit digits ("1,250.50"). Anything else ("1.5" for VND) is
 * refused rather than guessed.
 */
export function parseAmount(text: string, currency: string): string | null {
  const minor = minorUnits(currency);
  const t = text.trim();
  const grouped = minor === 0 ? /^(\d+|\d{1,3}([., ]\d{3})+)$/ : new RegExp(`^(\\d+|\\d{1,3}([, ]\\d{3})+)(\\.\\d{1,${minor}})?$`);
  if (!grouped.test(t)) return null;
  const plain = minor === 0 ? t.replace(/[., ]/g, '') : t.replace(/[, ]/g, '');
  const [whole = '', fraction = ''] = plain.split('.');
  const frac = fraction.replace(/0+$/, '');
  const value = `${whole.replace(/^0+(?=\d)/, '')}${frac ? `.${frac}` : ''}`;
  return /[1-9]/.test(value) ? value : null;
}

/** The request for a draft, or what is missing. */
export function adjustmentRequest(d: AdjustDraft): { request: WalletAdjustmentRequest } | { problem: AdjustProblem } {
  const amount = parseAmount(d.amount, d.currency);
  if (!amount) return { problem: 'amount' };
  if ((amount.split('.')[0] ?? '').length > MAX_INTEGER_DIGITS) return { problem: 'amountTooLarge' };
  const reason = d.reason.trim();
  if (!reason) return { problem: 'reason' };
  const reference = d.reference.trim();
  if (!reference) return { problem: 'reference' };
  return { request: { amount: d.direction === 'DEBIT' ? `-${amount}` : amount, currency: d.currency, reason, reference } };
}

/**
 * Whether to offer "Điều chỉnh số dư": a platform-wide (`Global`) staff grant holding
 * `billing.adjust`, as the server requires (the money comes from the platform's manual clearing;
 * a grant scoped to a customer is refused). Nothing is offered while `/me` loads (`grants` null)
 * or to customer users. The server checks the MFA session itself.
 */
export function mayAdjustWallet(grants: readonly { scope: string; permissions: readonly string[] }[] | null, client: boolean): boolean {
  return !client && Boolean(grants?.some((g) => g.scope === 'Global' && g.permissions.includes('billing.adjust')));
}
